// SalaryRevisionTab.jsx
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  LinearProgress
} from '@mui/material';
import {
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Percent as PercentIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import axios from 'axios';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontWeight: 600
  }
}));

const IncrementChip = styled(Chip)(({ theme, increment }) => ({
  backgroundColor: increment > 0 ? theme.palette.success.light : theme.palette.error.light,
  color: increment > 0 ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 600
}));

const SalaryRevisionTab = () => {
  const theme = useTheme();
  
  // State for employee selection
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  
  // State for revisions
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState(null);
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('all');

  // Summary statistics
  const [summary, setSummary] = useState({
    totalRevisions: 0,
    averageIncrement: 0,
    totalIncrementAmount: 0,
    maxIncrement: 0,
    minIncrement: 0
  });

  // Get company ID from localStorage
  const getCompanyId = () => {
    return localStorage.getItem('company_id') || '1';
  };

  // Fetch employees on component mount
  useEffect(() => {
    fetchEmployees();
  }, []);

  // Fetch revisions when employee is selected
  useEffect(() => {
    if (selectedEmployee) {
      fetchEmployeeDetails();
      fetchRevisionHistory();
    } else {
      setRevisions([]);
      setEmployeeDetails(null);
      setSearchTerm('');
      setDateRange('all');
      setSummary({
        totalRevisions: 0,
        averageIncrement: 0,
        totalIncrementAmount: 0,
        maxIncrement: 0,
        minIncrement: 0
      });
    }
  }, [selectedEmployee]);

  // Calculate summary when revisions change
  useEffect(() => {
    if (revisions.length > 0) {
      const increments = revisions.map(r => r.increment_amount || 0);
      const percentages = revisions.map(r => r.increment_percentage || 0);
      
      setSummary({
        totalRevisions: revisions.length,
        totalIncrementAmount: increments.reduce((a, b) => a + b, 0),
        averageIncrement: increments.reduce((a, b) => a + b, 0) / revisions.length,
        maxIncrement: Math.max(...increments),
        minIncrement: Math.min(...increments),
        averagePercentage: percentages.reduce((a, b) => a + b, 0) / percentages.length
      });
    }
  }, [revisions]);

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    setError(null);
    try {
      const companyId = getCompanyId();
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/revision/employees`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.employees) {
        setEmployees(response.data.employees);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      //console.error('Error fetching employees:', error);
      setError('Failed to load employees list. Please try again.');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchEmployeeDetails = async () => {
    if (!selectedEmployee) return;
    
    try {
      const companyId = getCompanyId();
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/revision/employees/${selectedEmployee}`, {
        
          headers: {
            Authorization: `Bearer ${token}`
          }
        
      });
      
      setEmployeeDetails(response.data.employee || null);
    } catch (error) {
      //console.error('Error fetching employee details:', error);
      setEmployeeDetails(null);
    }
  };

  const fetchRevisionHistory = async () => {
    if (!selectedEmployee) return;
    
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const companyId = getCompanyId();
      
      const response = await axios.get(`${API_BASE_URL}/api/revision/payroll/revision-history`, {
        params: { 
          employee_id: selectedEmployee, 
      
        },
        
          headers: {
            Authorization: `Bearer ${token}`
          }
        
      });
      
      
      // Ensure we have an array of revisions
      const revisionsData = response.data.revisions || [];
      
      // Process revisions to ensure they have all required fields
      const processedRevisions = revisionsData.map(rev => ({
        ...rev,
        // Ensure numeric values are numbers
        old_ctc: Number(rev.old_ctc) || 0,
        new_ctc: Number(rev.new_ctc) || 0,
        increment_amount: Number(rev.increment_amount) || 0,
        increment_percentage: Number(rev.increment_percentage) || 0,
        // Ensure salary_breakdown exists
        salary_breakdown: rev.salary_breakdown || { old: {}, new: {} }
      }));
      
      setRevisions(processedRevisions);
      setPage(0); // Reset to first page
    } catch (error) {
      //console.error('Error fetching revisions:', error);
      setError('Failed to load salary revision history. Please try again.');
      setRevisions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (revision) => {
    setSelectedRevision(revision);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRevision(null);
  };

  const handleEmployeeChange = (event) => {
    setSelectedEmployee(event.target.value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleDateRangeChange = (event) => {
    setDateRange(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    fetchRevisionHistory();
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN').format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Filter revisions based on search and date range
  const filteredRevisions = revisions.filter(revision => {
    // Search filter
    const matchesSearch = searchTerm === '' || 
      (revision.change_reason?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      formatDate(revision.effective_from).includes(searchTerm) ||
      formatCurrency(revision.old_ctc).includes(searchTerm) ||
      formatCurrency(revision.new_ctc).includes(searchTerm);
    
    // Date range filter
    let matchesDateRange = true;
    if (dateRange !== 'all' && revision.effective_from) {
      try {
        const revisionDate = new Date(revision.effective_from);
        const now = new Date();
        const monthsAgo = new Date();
        monthsAgo.setMonth(now.getMonth() - parseInt(dateRange));
        matchesDateRange = revisionDate >= monthsAgo;
      } catch (error) {
        matchesDateRange = true;
      }
    }
    
    return matchesSearch && matchesDateRange;
  });

  // Get all salary components from your model
  const getSalaryComponents = () => {
    if (!selectedRevision?.salary_breakdown) return [];
    
    // Define all the components you want to display (from your model)
    const components = [
      { key: 'base_salary', label: 'Base Salary' },
      { key: 'hra', label: 'HRA' },
      { key: 'special_allowance', label: 'Special Allowance' },
      { key: 'performance_bonus', label: 'Performance Bonus' },
      { key: 'gratuity', label: 'Gratuity' },
      { key: 'medicalInsurance', label: 'Medical Insurance' },
      { key: 'employeepf', label: 'Employee PF' },
      { key: 'employerpf', label: 'Employer PF' },
      { key: 'professional_tax', label: 'Professional Tax' },
      { key: 'gross_salary', label: 'Gross Salary' },
      { key: 'taxable_income', label: 'Taxable Income' },
      { key: 'variable_salary', label: 'Variable Salary' },
      { key: 'joining_bonus', label: 'Joining Bonus' },
      { key: 'total_tax', label: 'Total Tax' },
      { key: 'monthly_tax', label: 'Monthly Tax' },
      { key: 'pfno', label: 'PF Number' },
      { key: 'uan', label: 'UAN' }
    ];
    
    // Filter out components that have no data in either old or new
    return components.filter(comp => {
      const oldValue = selectedRevision.salary_breakdown.old?.[comp.key];
      const newValue = selectedRevision.salary_breakdown.new?.[comp.key];
      return oldValue !== undefined || newValue !== undefined;
    });
  };

  // Export to PDF function
  const exportToPDF = () => {
    if (!selectedEmployee || filteredRevisions.length === 0) return;
    
    const doc = new jsPDF('landscape');
    
    // Add title
    doc.setFontSize(18);
    doc.text('Salary Revision History', 14, 22);
    doc.setFontSize(11);
    doc.text(`Employee:  ${selectedEmployee}`, 14, 30);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 36);
    
    // Add summary statistics
    doc.setFontSize(10);
    doc.text(`Total Revisions: ${summary.totalRevisions}`, 14, 45);
    doc.text(`Total Increment: ${summary.totalIncrementAmount}`, 14, 51);
    doc.text(`Avg Increment: ${summary.averageIncrement}`, 80, 51);
    doc.text(`Max Increment: ${summary.maxIncrement}`, 146, 51);
    doc.text(`Min Increment: ${summary.minIncrement}`, 212, 51);
    
    // Table headers
    const tableColumn = [
      "S.No",
      "Effective Date",
      "Old CTC",
      "New CTC",
      "Increment Amount",
      "Increment %",
    ];
    
    // Create table rows
    const tableRows = filteredRevisions.map((rev, index) => [
      String(index + 1),
      formatDate(rev.effective_from),
      rev.old_ctc,
      rev.new_ctc,
      rev.increment_amount,
      `${rev.increment_percentage?.toFixed(2) || 0}%`,
    ]);
    
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 60,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
      columnStyles: {
        0: { cellWidth: 15 },
        1: { cellWidth: 30 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 35 },
        5: { cellWidth: 25 },
      }
    });
    
    doc.save(`Salary_Revision_${selectedEmployee}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // Export to Excel function
  const exportToExcel = () => {
    if (!selectedEmployee || filteredRevisions.length === 0) return;
    
    const worksheet = XLSX.utils.json_to_sheet(
      filteredRevisions.map((rev, index) => ({
        'S.No': index + 1,
        'Effective Date': formatDate(rev.effective_from),
        'Old CTC': rev.old_ctc,
        'New CTC': rev.new_ctc,
        'Increment Amount': rev.increment_amount,
        'Increment Percentage': rev.increment_percentage,
        'Old Base Salary': rev.salary_breakdown?.old?.base_salary || 0,
        'New Base Salary': rev.salary_breakdown?.new?.base_salary || 0,
        'Old HRA': rev.salary_breakdown?.old?.hra || 0,
        'New HRA': rev.salary_breakdown?.new?.hra || 0,
        'Old Special Allowance': rev.salary_breakdown?.old?.special_allowance || 0,
        'New Special Allowance': rev.salary_breakdown?.new?.special_allowance || 0,
        'Old Gross Salary': rev.salary_breakdown?.old?.gross_salary || 0,
        'New Gross Salary': rev.salary_breakdown?.new?.gross_salary || 0
      }))
    );
    
    // Adjust column widths
    const wscols = [
      { wch: 5 },  // S.No
      { wch: 15 }, // Effective Date
      { wch: 15 }, // Old CTC
      { wch: 15 }, // New CTC
      { wch: 15 }, // Increment Amount
      { wch: 12 }, // Increment Percentage
      { wch: 15 }, // Old Base Salary
      { wch: 15 }, // New Base Salary
      { wch: 15 }, // Old HRA
      { wch: 15 }, // New HRA
      { wch: 18 }, // Old Special Allowance
      { wch: 18 }, // New Special Allowance
      { wch: 15 }, // Old Gross Salary
      { wch: 15 }  // New Gross Salary
    ];
    worksheet['!cols'] = wscols;
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Salary Revisions');
    XLSX.writeFile(workbook, `Salary_Revision_${selectedEmployee}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export all revisions summary for all employees (optional)
  const exportAllEmployeesToExcel = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const companyId = getCompanyId();
      const allRevisionsData = [];
      
      // Fetch revisions for all employees
      for (const emp of employees) {
        const response = await axios.get(`${API_BASE_URL}/api/revision/payroll/revision-history`, {
          params: { 
            employee_id: emp.employee_id, 
          
          },
          
            headers: {
              Authorization: `Bearer ${token}`
            }
          
        });
        
        const revisions = response.data.revisions || [];
        revisions.forEach(rev => {
          allRevisionsData.push({
            'Employee ID': emp.employee_id,
            'Employee Name': `${emp.firstName} ${emp.lastName}`,
            'Department': emp.department || 'N/A',
            'Designation': emp.designation || 'N/A',
            'Effective Date': formatDate(rev.effective_from),
            'Old CTC': rev.old_ctc || 0,
            'New CTC': rev.new_ctc || 0,
            'Increment Amount': rev.increment_amount || 0,
            'Increment Percentage': rev.increment_percentage || 0,
            'Change Reason': rev.change_reason || 'Not specified'
          });
        });
      }
      
      if (allRevisionsData.length === 0) {
        alert('No revision data found for any employee');
        return;
      }
      
      const worksheet = XLSX.utils.json_to_sheet(allRevisionsData);
      const wscols = [
        { wch: 12 }, // Employee ID
        { wch: 25 }, // Employee Name
        { wch: 15 }, // Department
        { wch: 20 }, // Designation
        { wch: 15 }, // Effective Date
        { wch: 15 }, // Old CTC
        { wch: 15 }, // New CTC
        { wch: 15 }, // Increment Amount
        { wch: 12 }, // Increment Percentage
        { wch: 30 }  // Change Reason
      ];
      worksheet['!cols'] = wscols;
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'All Salary Revisions');
      XLSX.writeFile(workbook, `All_Salary_Revisions_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      //console.error('Error exporting all revisions:', error);
      setError('Failed to export all revisions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Employee selector loading state
  if (loadingEmployees) {
    return (
      <Paper sx={{ p: 3, m: 2 }}>
        <Typography variant="h5" component="h2" fontWeight="600" gutterBottom>
          Salary Revision History
        </Typography>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      {/* Header with Employee Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Salary Revision History
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          View and analyze salary revision history for employees
        </Typography>

        {/* Employee Selector */}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={selectedEmployee ? 4 : 12} lg={selectedEmployee ? 3 : 8}>
            <FormControl fullWidth size="small">
              <InputLabel>Select Employee</InputLabel>
              <Select
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                label="Select Employee"
                startAdornment={
                  <InputAdornment position="start">
                    <PersonIcon />
                  </InputAdornment>
                }
              >
                <MenuItem value="">
                  <em>Select an employee</em>
                </MenuItem>
                {employees.length > 0 ? (
                  employees.map((emp) => (
                    <MenuItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.firstName} {emp.lastName} - {emp.employee_id}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No employees found</MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          
          {selectedEmployee && (
            <>
              <Grid item xs={12} md={3} lg={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by reason, date or amount"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              
              <Grid item xs={6} md={2} lg={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    label="Date Range"
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="3">Last 3 Months</MenuItem>
                    <MenuItem value="6">Last 6 Months</MenuItem>
                    <MenuItem value="12">Last 12 Months</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={6} md={3} lg={4}>
                <Box display="flex" gap={1} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={exportToExcel}
                    disabled={filteredRevisions.length === 0 || loading}
                    size="small"
                  >
                    Excel
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={exportToPDF}
                    disabled={filteredRevisions.length === 0 || loading}
                    size="small"
                  >
                    PDF
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={loading}
                    size="small"
                  >
                    Refresh
                  </Button>
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {/* Employee Summary Card */}
      {employeeDetails && (
        <Card sx={{ 
          mb: 4, 
          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
          color: 'white'
        }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Avatar sx={{ bgcolor: 'white', color: theme.palette.primary.main, width: 56, height: 56 }}>
                  <PersonIcon />
                </Avatar>
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="600">
                  {employeeDetails.firstName} {employeeDetails.lastName}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                  Employee ID: {employeeDetails.employee_id}
                </Typography>
                {(employeeDetails.department || employeeDetails.designation) && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {employeeDetails.department && `Department: ${employeeDetails.department}`}
                    {employeeDetails.department && employeeDetails.designation && ' | '}
                    {employeeDetails.designation && `Designation: ${employeeDetails.designation}`}
                  </Typography>
                )}
              </Grid>
              <Grid item>
                <Chip 
                  label={`Total Revisions: ${filteredRevisions.length}`}
                  sx={{ 
                    bgcolor: 'white', 
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 2 }
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards for selected employee */}
      {selectedEmployee && filteredRevisions.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: '#1565c0' }}>
                  Total Revisions
                </Typography>
                <Typography variant="h5" fontWeight="600" sx={{ color: '#1565c0' }}>
                  {summary.totalRevisions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#e8f5e8' }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: '#2e7d32' }}>
                  Total Increment
                </Typography>
                <Typography variant="h6" fontWeight="600" sx={{ color: '#2e7d32' }}>
                  {formatCurrency(summary.totalIncrementAmount)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#fff3e0' }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: '#e65100' }}>
                  Average Increment
                </Typography>
                <Typography variant="h6" fontWeight="600" sx={{ color: '#e65100' }}>
                  {formatCurrency(summary.averageIncrement)}
                </Typography>
              
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#f3e5f5' }}>
              <CardContent>
                <Typography variant="overline" sx={{ color: '#6a1b9a' }}>
                  Max Increment
                </Typography>
                <Typography variant="h6" fontWeight="600" sx={{ color: '#6a1b9a' }}>
                  {formatCurrency(summary.maxIncrement)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }} 
          onClose={() => setError(null)}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* No Employee Selected */}
      {!selectedEmployee && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<InfoIcon />}
        >
          Please select an employee from the dropdown above to view their salary revision history.
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ width: '100%', mt: 3 }}>
          <LinearProgress sx={{ mb: 2 }} />
          <Box sx={{ mt: 2 }}>
            {[1, 2, 3].map((item) => (
              <Paper key={item} sx={{ p: 2, mb: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={2}><Skeleton variant="text" /></Grid>
                  <Grid item xs={2}><Skeleton variant="text" /></Grid>
                  <Grid item xs={2}><Skeleton variant="text" /></Grid>
                  <Grid item xs={2}><Skeleton variant="text" /></Grid>
                  <Grid item xs={3}><Skeleton variant="text" /></Grid>
                  <Grid item xs={1}><Skeleton variant="circular" width={40} height={40} /></Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* No Data State */}
      {selectedEmployee && !loading && filteredRevisions.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          {searchTerm || dateRange !== 'all' 
            ? 'No revisions match your current filters. Try adjusting your search criteria.'
            : 'No salary revision history found for this employee.'}
        </Alert>
      )}

      {/* Revisions Table */}
      {selectedEmployee && !loading && filteredRevisions.length > 0 && (
        <>
          <StyledTableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Effective Date</TableCell>
                  <TableCell align="right">Old CTC</TableCell>
                  <TableCell align="right">New CTC</TableCell>
                  <TableCell align="center">Increment</TableCell>
                  {/* <TableCell>Change Reason</TableCell> */}
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRevisions
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((revision) => (
                    <TableRow 
                      key={revision.revision_id || Math.random()}
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                          <Typography variant="body2">
                            {formatDate(revision.effective_from)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="500">
                          {formatCurrency(revision.old_ctc)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="600" color="primary.main">
                          {formatCurrency(revision.new_ctc)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Typography 
                            variant="body2" 
                            fontWeight="600"
                            color={revision.increment_amount > 0 ? 'success.main' : 'error.main'}
                          >
                            {revision.increment_amount > 0 ? '+' : ''}
                            {formatCurrency(revision.increment_amount)}
                          </Typography>
                          <IncrementChip
                            label={`${revision.increment_percentage?.toFixed(2) || 0}%`}
                            increment={revision.increment_amount}
                            size="small"
                            icon={revision.increment_amount > 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                          />
                        </Box>
                      </TableCell>
                  
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton
                            color="primary"
                            onClick={() => handleViewDetails(revision)}
                            size="small"
                            sx={{ 
                              bgcolor: theme.palette.primary.light,
                              color: 'white',
                              '&:hover': {
                                bgcolor: theme.palette.primary.main
                              }
                            }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredRevisions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        </>
      )}

      {/* Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        scroll="paper"
        TransitionProps={{
          timeout: 300
        }}
      >
        {selectedRevision && (
          <>
            <DialogTitle sx={{ 
              pb: 1,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="600">
                  Salary Revision Details
                </Typography>
                <IconButton onClick={handleCloseModal} size="small" sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 3 }}>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography color="text.secondary" variant="body2">
                          Old CTC
                        </Typography>
                      </Box>
                      <Typography variant="h5" fontWeight="600">
                        {formatCurrency(selectedRevision.old_ctc)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ borderColor: 'primary.main', height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <MoneyIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography color="primary.main" variant="body2">
                          New CTC
                        </Typography>
                      </Box>
                      <Typography variant="h5" fontWeight="600" color="primary.main">
                        {formatCurrency(selectedRevision.new_ctc)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TrendingUpIcon sx={{ mr: 1, color: 'success.main' }} />
                        <Typography color="success.main" variant="body2">
                          Increment
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography variant="h5" fontWeight="600" color="success.main">
                          {formatCurrency(selectedRevision.increment_amount)}
                        </Typography>
                        <Chip
                          label={`${selectedRevision.increment_percentage?.toFixed(2) || 0}%`}
                          color="success"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Salary Component Comparison */}
              {selectedRevision.salary_breakdown && 
               (Object.keys(selectedRevision.salary_breakdown.old || {}).length > 0 || 
                Object.keys(selectedRevision.salary_breakdown.new || {}).length > 0) ? (
                <>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                    Salary Component Comparison
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
                          <TableCell>Component</TableCell>
                          <TableCell align="right">Old Amount</TableCell>
                          <TableCell align="right">New Amount</TableCell>
                          <TableCell align="right">Difference</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getSalaryComponents().map(({ key, label }) => {
                          const oldValue = selectedRevision.salary_breakdown.old?.[key] || 0;
                          const newValue = selectedRevision.salary_breakdown.new?.[key] || 0;
                          const difference = newValue - oldValue;
                          
                          // Skip if both values are 0 and it's not a text field
                          if (oldValue === 0 && newValue === 0 && key !== 'pfno' && key !== 'uan') return null;
                          
                          // Handle text fields differently
                          if (key === 'pfno' || key === 'uan') {
                            return (
                              <TableRow key={key} hover>
                                <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                                  {label}
                                </TableCell>
                                <TableCell align="right">
                                  {oldValue || '-'}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography color="primary.main" fontWeight="500">
                                    {newValue || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  {oldValue !== newValue && (
                                    <Typography color="info.main" fontWeight="500">
                                      Updated
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          }
                          
                          return (
                            <TableRow key={key} hover>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                                {label}
                              </TableCell>
                              <TableCell align="right">
                                {oldValue ? formatCurrency(oldValue) : '-'}
                              </TableCell>
                              <TableCell align="right">
                                <Typography color="primary.main" fontWeight="500">
                                  {newValue ? formatCurrency(newValue) : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {difference !== 0 && (
                                  <Typography
                                    color={difference > 0 ? 'success.main' : 'error.main'}
                                    fontWeight="500"
                                  >
                                    {difference > 0 ? '+' : ''}{formatCurrency(difference)}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        
                        {/* Total row */}
                        <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 700 }}>
                            TOTAL CTC
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(selectedRevision.old_ctc)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            {formatCurrency(selectedRevision.new_ctc)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            <Typography
                              color={selectedRevision.increment_amount > 0 ? 'success.main' : 'error.main'}
                              fontWeight="700"
                            >
                              {selectedRevision.increment_amount > 0 ? '+' : ''}
                              {formatCurrency(selectedRevision.increment_amount)}
                              <Chip
                                label={`${selectedRevision.increment_percentage?.toFixed(2) || 0}%`}
                                color={selectedRevision.increment_amount > 0 ? 'success' : 'error'}
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Detailed salary breakdown is not available for this revision.
                </Alert>
              )}

              {/* Revision Details */}
              <Box sx={{ bgcolor: theme.palette.grey[50], p: 2, borderRadius: 1 }}>
                <Grid container spacing={2}>
                 
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Effective From:</strong> {formatDate(selectedRevision.effective_from)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Employee ID:</strong> {selectedEmployee}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseModal} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default SalaryRevisionTab;